import type { CSSInterpolation } from '@emotion/serialize';

import * as colorLibrary from '@davidlloydleisure/common-js-frontend/dist/sass/colors.module.scss';
import * as fontLibrary from '@davidlloydleisure/common-js-frontend/dist/sass/fonts.module.scss';

import type { Styling } from '../../src/context/styling';

import { mq } from './media-queries';
import * as zIndexLibrary from './z-indexes.module.scss';

// Identity function with types that infer the correct keys of our object, but explicitly specify that the
// values are CSS styles (Interpolation is Emotion's CSS style type)
const stylesObject = <T>(x: { [k in keyof T]: CSSInterpolation }) => x;

// Values
export const colors = {
  alert: colorLibrary.alert,
  ash: colorLibrary.ash,
  beige: colorLibrary.beige,
  beigeGrey: colorLibrary.beigeGrey,
  blazeDividerGray: colorLibrary.blazeDividerGray,
  blazeGray: colorLibrary.blazeGray,
  blazeIconBackgroundGray: colorLibrary.blazeIconBackgroundGray,
  borderGrey: colorLibrary.borderGrey,
  bronze: colorLibrary.bronze,
  buttonDisabled: colorLibrary.buttonDisabledColor,
  camelot: colorLibrary.camelot,
  capeCod: colorLibrary.capeCod,
  cardBackground: colorLibrary.cardBackground,
  clubFinderPrimaryGreen: colorLibrary.clubFinderPrimaryGreen,
  clubFinderSearchGrey: colorLibrary.clubFinderSearchGrey,
  clubFinderSecondaryGreen: colorLibrary.clubFinderSecondaryGreen,
  clubFinderTextGrey: colorLibrary.clubFinderTextGrey,
  placeHolderTextGrey: colorLibrary.placeHolderTextGrey,
  comingSoonBorderPurple: colorLibrary.comingSoonBorderPurple,
  comingSoonPurple: colorLibrary.comingSoonPurple,
  copyrightGray: colorLibrary.copyrightGray,
  cream: colorLibrary.cream,
  darkGreen: colorLibrary.darkGreen,
  davidLloydBrown: colorLibrary.davidLloydBrown,
  davidLloydGray: colorLibrary.davidLloydGray,
  davidLloydPurple: colorLibrary.davidLloydPurple,
  davidLloydSecondaryPurple: colorLibrary.davidLloydSecondaryPurple,
  davidLloydTeal: colorLibrary.davidLloydTeal,
  davidLloydTransparentPurple: colorLibrary.davidLloydTransparentPurple,
  disabledBackground: colorLibrary.disabledBackground,
  disabledText: colorLibrary.disabledText,
  defaultPromoTextColor: colorLibrary.defaultPromoTextColor,
  fadedHarbourGold: colorLibrary.fadedHarbourGold,
  fadedHarbourSlateGrey: colorLibrary.fadedHarbourSlateGrey,
  grayOlive: colorLibrary.grayOlive,
  transparentGrayOlive: colorLibrary.transparentGrayOlive,
  transparentGreenGrey: colorLibrary.transparentGreenGrey,
  greyedOutBackground: colorLibrary.greyedOutBackground,
  harbourDarkGold: colorLibrary.harbourDarkGold,
  harbourGold: colorLibrary.harbourGold,
  harbourHeaderBorder: colorLibrary.harbourHeaderBorder,
  harbourSlateGrey: colorLibrary.harbourSlateGrey,
  hessianBackground: colorLibrary.hessianBackground,
  highlightGold: colorLibrary.highlightGold,
  introText: colorLibrary.introText,
  lightGray: colorLibrary.lightGray,
  lightSilver: colorLibrary.lightSilver,
  mediumGray: colorLibrary.mediumGray,
  membershipSummaryDarkGray: colorLibrary.membershipSummaryDarkGray,
  meridianBackgroundLight: colorLibrary.meridianBackgroundLight,
  meridianCoolGray: colorLibrary.meridianCoolGray,
  meridianDarkCoolGray: colorLibrary.meridianDarkCoolGray,
  meridianDarkGrayFooter: colorLibrary.meridianDarkGrayFooter,
  meridianDivider: colorLibrary.meridianDivider,
  meridianFieryYellow: colorLibrary.meridianFieryYellow,
  meridianHighlightGrey: colorLibrary.meridianHighlightGrey,
  meridianLightGray: colorLibrary.meridianLightGray,
  meridianLightGrayFooter: colorLibrary.meridianLightGrayFooter,
  meridianSecondaryBackground: colorLibrary.meridianSecondaryBackground,
  meridianSecondaryHighlight: colorLibrary.meridianSecondaryHighlight,
  meridianPopupSecondaryActionBackground: colorLibrary.meridianPopupSecondaryActionBackground,
  meridianPopupSecondaryActionBorder: colorLibrary.meridianPopupSecondaryActionBorder,
  meridianSlateGrey: colorLibrary.meridianSlateGrey,
  paleGrey: colorLibrary.paleGrey,
  paleRed: colorLibrary.paleRed,
  payByLinkBlockGrey: colorLibrary.payByLinkBlockGrey,
  silver: colorLibrary.silver,
  transparentGreenGreyBackground: colorLibrary.transparentGreenGreyBackground,
  brightGrey: colorLibrary.brightGrey,
  vibrant: colorLibrary.vibrant,
  brightYellow: colorLibrary.brightYellow,
  sageBackground: colorLibrary.sageBackground,
  greyD: colorLibrary.greyD,
  lightGreen: colorLibrary.lightGreen,
  error: colorLibrary.error,
  errorBoundary: colorLibrary.errorBoundary,
  pinBorder: colorLibrary.pinBorder,
  inputFocusOutline: colorLibrary.inputFocusOutline,
  inputActiveBackground: colorLibrary.inputActiveBackground,
  bannerPlum: colorLibrary.bannerPlum,
  bannerGreen: colorLibrary.bannerGreen,
  meridianOrange: colorLibrary.meridianOrange,
  meridianOrangeHover: colorLibrary.meridianOrangeHover,
  meridianBlue: colorLibrary.meridianBlue,
  meridianBrightBlue: colorLibrary.meridianBrightBlue,
  meridianFadeBlue: colorLibrary.meridianFadeBlue,
  meridianPaleOrange: colorLibrary.meridianPaleOrange,
  iconChatColor: colorLibrary.iconChatColor,
  harbourHoverGrey: colorLibrary.harbourHoverGrey,
  harbourHoverGold: colorLibrary.harbourHoverGold,
  paleGold: colorLibrary.paleGold,
  white: colorLibrary.white,
  fenceGreen: '#0A3C36', //TODO add to color library
};

export const spacing = {
  none: 0,
  extraSmall: '.25rem',
  small: '.5rem',
  medium: '1rem',
  large: '2rem',
  extraLarge: '4rem',
  extraExtraLarge: '8rem',
  extraExtraExtraLarge: '16rem',
};

export const calculateDesktopGridWidth = (parentColumns: number, childColumns: number) => {
  const gutterWidth = 24;
  const columnWidth = 88;

  const childWidth = childColumns * columnWidth + (childColumns - 1) * gutterWidth;
  const parentWidth = parentColumns * columnWidth + (parentColumns - 1) * gutterWidth;

  return (100 * childWidth) / parentWidth + '%';
};

export const fontSizes = {
  extraSmall: '.25rem',
  small: '.5rem',
  medium: '1rem',
  large: '2rem',
  extraLarge: '4rem',
};

export const zIndexes = {
  dropShadow: Number(zIndexLibrary.dropShadow),
  pageNavigation: Number(zIndexLibrary.pageNavigation),
  breadcrumb: Number(zIndexLibrary.breadcrumb),
  dropdown: Number(zIndexLibrary.dropdown),
  overlay: Number(zIndexLibrary.overlay),
  sliderOverlay: Number(zIndexLibrary.sliderOverlay),
  clubFacilitiesHeader: Number(zIndexLibrary.clubFacilitiesHeader),
  cookies: Number(zIndexLibrary.cookies),
  header: Number(zIndexLibrary.header),
  popup: Number(zIndexLibrary.popup),
  recaptchaBadgePopup: Number(zIndexLibrary.recaptchaBadgePopup),
  mobileNav: Number(zIndexLibrary.mobileNav),
  popupCloseButton: Number(zIndexLibrary.popupCloseButton),
  wholePagePopup: Number(zIndexLibrary.wholePagePopup),

  chatIcon: Number(zIndexLibrary.chatIcon),
  chatOverlay: Number(zIndexLibrary.chatOverlay),
  wholePageOverlay: Number(zIndexLibrary.wholePageOverlay),
};

export const borderRadius = {
  button: '0.2rem',
  roundedCorner: '.25rem',
  basicButton: '0.1875rem',
  redesignButton: '3.75rem',
  meridianButton: '0.3rem',
  redesignMeridianButton: '0.5rem',
  container: '0.3rem',
  accordion: '0.625rem', // 10px
  narrowImage: '0.87056rem',
  navigationBlock: '1.67656rem',
  image: {
    small: '0.375rem', // 6px
    medium: '0.75rem', // 12px
    large: '1.5rem', // 24px
  },
};

// Styles
export const maxPageWidth = mq({
  maxWidth: [null, '70rem'],
  paddingLeft: [null, '1rem'],
  paddingRight: [null, '1rem'],
  marginLeft: [null, 'auto'],
  marginRight: [null, 'auto'],
});

type Font = {
  fontFamily: string;
  fontWeight: number | string;
};

export const fonts = {
  regular: {
    fontFamily: fontLibrary.fontStack,
    fontWeight: fontLibrary.regularFontWeight,
  },
  light: {
    fontFamily: fontLibrary.fontStack,
    fontWeight: fontLibrary.lightFontWeight,
  },
  bold: {
    fontFamily: fontLibrary.fontStack,
    fontWeight: fontLibrary.boldFontWeight,
  },
  harbour: {
    fontFamily: fontLibrary.harbourRegularFontStack,
    fontWeight: fontLibrary.harbourRegularFontWeight,
  },
  harbourLight: {
    fontFamily: fontLibrary.harbourRegularFontStack,
    fontWeight: fontLibrary.harbourRegularFontWeight,
  },
  harbourBold: {
    fontFamily: fontLibrary.harbourBoldFontStack,
    fontWeight: fontLibrary.harbourBoldFontWeight,
  },
} as const satisfies Record<string, Font>;

export const flex = stylesObject({
  column: {
    display: 'flex',
    flexDirection: 'column',
  },
  columnCentered: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  columnSpacedOut: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
  },
  rowCentered: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  rowSpacedOut: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  centerDisplay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const FORM_FIELD_HEIGHT_IN_REM = 3.35;
export const formFieldHeight = `${FORM_FIELD_HEIGHT_IN_REM}rem`;

const defaultShadow = '0 10px 35px 0 rgba(0, 0, 0, 0.1), 0 4px 7px 0 rgba(0, 0, 0, 0.04)';
export const shadow = {
  regular: {
    boxShadow: defaultShadow,
  },
  regularDesktop: mq({
    boxShadow: ['none', defaultShadow],
  }),
  below: {
    boxShadow: '0px 10px 20px 0px rgba(0, 0, 0, 0.05)',
  },
  subtleBelow: {
    boxShadow: '0px 10px 20px -20px rgba(0, 0, 0, 0.2)',
  },
  above: {
    boxShadow: '0px -10px 20px 0px rgba(0, 0, 0, 0.05)',
  },
  around: {
    boxShadow: '4px 4px 20px 1px rgba(0, 0, 0, 0.2)',
  },
  headerBelow: mq({
    boxShadow: ['none', '0 5px 10px 0 rgba(0, 0, 0, 0.1)'],
  }),
  small: {
    boxShadow: '0 5px 20px 0 rgba(0, 0, 0, 0.1), 0 4px 7px 0 rgba(0, 0, 0, 0.04)',
  },
};

export const errorMessageStyling = (styling: Styling): CSSInterpolation => [
  maxPageWidth,
  styling.fonts.regular,
  {
    padding: '1rem',
    margin: '2rem auto',
    lineHeight: 1.5,
    fontSize: '1rem',
    textAlign: 'center',
    color: styling.colors.bodyText,
  },
];

export const clubFacilitiesMobileNavHeight = '2.6rem';

export const mainNavHeight = '4.5rem';
export const globalBannerHeight = {
  mobile: '4rem',
  default: '4.5rem',
};

// We reduce the height of sticky items to handle cases where the browser renderer causes an extra
// pixel spacing above the sticky item to appear
export const STACKED_STICKY_ITEM_HEIGHT_REDUCTION = 1;
